import {type MediaCardType} from '@wandb/weave/common/types/media';
import React, {createContext, useCallback, useContext, useMemo} from 'react';

import {
  type SettingType,
  trackSetting,
} from '../../../services/analytics/workspaceSettingsEvents';
import {useViewAction} from '../../../state/views/hooks';
import * as ViewHooks from '../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import {useSectionSettings} from '../../../state/views/panelBankSectionConfig/selectors';
import {type Ref as SectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {type DispatchableAction} from '../../../types/redux';
import {type OrganizedSettings} from '../../PanelBank/types';
import {type MediaBrowserPanelConfig} from '../../PanelMediaBrowser';
import {useSetSectionSyncSliderSteps} from '../../PanelMediaBrowser/hooks/useSyncSliderSteps';
import {useMediaCardTypes} from '../../PanelMediaBrowser/useGetMediaCardTypes';
import {useWorkspaceInheritedSettings} from './hooks/useWorkspaceInheritedSettings';
import {useSectionLinePlotSettings} from './runLinePlots/utils/getLinePlotSettings';
import {type Complete, type LinePlotSettings} from './types';
import {useWorkspaceSettingsContext} from './WorkspaceSettingsContextProvider';

export type SectionSettingsContextType = {
  /**
   * Set of settings that are either inherited from workspace or defined at the section level.
   */
  inheritedSettings: OrganizedSettings;
  /**
   * Section level settings defined in view spec
   */
  linePlot: Complete<LinePlotSettings>;
  sectionRef: SectionRef;
  updateLinePlotSectionSettings: (
    settings: Partial<LinePlotSettings>
  ) => DispatchableAction;
  trackSectionSettingChange: (
    setting: SettingType,
    value: string | undefined
  ) => void;
  /**
   * Media card types that are currently in the panels that are in the section.
   */
  mediaCardTypes: MediaCardType[];
  /**
   * Media keys that are currently in the panels that are in the section.
   */
  mediaKeys: string[];
  /**
   * Media settings for the section.
   */
  sectionMediaSettings: MediaBrowserPanelConfig;
  /**
   * Sync the section's panels slider steps
   */
  setSectionSliderSteps: (stepIndex: number, mediaSliderKey: string) => void;
};

export const SectionSettingsContext = createContext<
  SectionSettingsContextType | undefined
>(undefined);

type SectionSettingsProps = {
  sectionRef: SectionRef;
  children: React.ReactNode;
};

/**
 * This context provider is used in conjuction with WorkspaceSettingsContextProvider.
 * We use these two context providers to handle cascading settings logic.
 */
export const SectionSettingsContextProvider = ({
  sectionRef,
  children,
}: SectionSettingsProps) => {
  const workspaceSettingsContext = useWorkspaceSettingsContext();
  const sectionSettings = useSectionSettings(sectionRef);
  const sectionLinePlotSettings = useSectionLinePlotSettings(sectionSettings);
  const sectionMediaSettings = sectionSettings?.media;

  // This hook determines if workspace or section setting should be used.
  // We want UX to reflect if section level is using workspace level setting or not.
  const inheritedSettings = useWorkspaceInheritedSettings(
    workspaceSettingsContext,
    sectionLinePlotSettings,
    sectionMediaSettings
  );

  const updateLinePlotSectionSettings = useViewAction(
    sectionRef,
    PanelBankSectionConfigActions.updateLinePlotSectionSettings
  );

  const section = ViewHooks.usePart(sectionRef);
  const panels = ViewHooks.useParts(section.panelRefs);
  const mediaKeys = useMemo(
    () =>
      Array.from(
        panels.reduce((mediaKeys, panel) => {
          if (panel.viewType === 'Media Browser' && panel.config.mediaKeys) {
            panel.config.mediaKeys.forEach(key => {
              mediaKeys.add(key);
            });
          }
          return mediaKeys;
        }, new Set<string>())
      ),
    [panels]
  );
  const mediaCardTypes = useMediaCardTypes(mediaKeys);

  // analytics
  const trackSectionSettingChange = useCallback(
    (setting: SettingType, value: string | undefined) => {
      trackSetting({
        action: 'change',
        page: 'workspace',
        settingLevel: 'section',
        setting,
        settingValue: value,
      });
    },
    []
  );

  const setSectionSliderSteps = useSetSectionSyncSliderSteps(
    workspaceSettingsContext?.media?.workspaceMediaSettings,
    sectionMediaSettings,
    section,
    panels
  );

  const value = useMemo(
    () => ({
      inheritedSettings,
      linePlot: sectionLinePlotSettings,
      sectionRef,
      sectionSettings,
      trackSectionSettingChange,
      updateLinePlotSectionSettings,
      mediaCardTypes: mediaCardTypes ?? [],
      mediaKeys,
      sectionMediaSettings: sectionMediaSettings ?? {},
      setSectionSliderSteps,
    }),
    [
      inheritedSettings,
      sectionLinePlotSettings,
      sectionRef,
      sectionSettings,
      trackSectionSettingChange,
      updateLinePlotSectionSettings,
      mediaCardTypes,
      mediaKeys,
      sectionMediaSettings,
      setSectionSliderSteps,
    ]
  );
  return (
    <SectionSettingsContext.Provider value={value}>
      {children}
    </SectionSettingsContext.Provider>
  );
};

export const useSectionSettingsContext = (): SectionSettingsContextType => {
  const value = useContext(SectionSettingsContext);

  if (value == null) {
    throw new Error(
      'useSectionSettingsContext must be used within a SectionSettingsContextProvider'
    );
  }

  return value;
};

export const useSectionSettingsContextMaybe = () => {
  return useContext(SectionSettingsContext);
};
